import React, { useState, useCallback, useRef, useEffect } from 'react'
import { message, Tooltip } from 'antd'
import { Popup } from 'antd-mobile'
import AsyncSearchSelect, { SearchOption } from '@/components/common/AsyncSearchSelect'
import AsyncSearchSelectResponsive from '@/components/common/AsyncSearchSelectResponsive'
import { TravelCompanion } from '@/types'
import { apiClient } from '@/services/api'
import { getCollectionFromDeptId } from '@/utils/collection'
import { useNoBudgetDepartments, NO_BUDGET_HINT, NO_BUDGET_TAG } from '@/hooks/useNoBudgetDepartments'
import type { EmployeeDTO, DepartmentDTO } from '@/types'

interface CompanionListProps {
  companions: TravelCompanion[]
  onChange: (companions: TravelCompanion[]) => void
  onValidationChange?: (isValid: boolean, message?: string) => void
  /** 申请人部门ID，用于部门搜索仅同主体（与申请人主体一致） */
  applicantDeptId?: string
  /** 申请人主体 ID，如 "GSX01"，优先于 applicantDeptId 用于部门搜索过滤 */
  applicantCollection?: string
  /** 查看模式下禁用编辑 */
  disabled?: boolean
}

/**
 * 同行人列表组件
 * 支持添加、删除同行人
 * 字段：同行人姓名、费用承担部门
 * 逻辑完全复用 BasicInfoSection 中的员工/部门搜索
 */
const CompanionList: React.FC<CompanionListProps> = ({
  companions,
  onChange,
  onValidationChange,
  applicantDeptId,
  applicantCollection,
  disabled = false,
}) => {
  const MAX_COMPANIONS = 40
  const [formKey, setFormKey] = useState(0)

  const { isDeptNoBudget } = useNoBudgetDepartments()

  // Ref 持有最新 companions，供 async 函数在 await 后读取，避免陈旧闭包导致并发变更丢失
  const companionsRef = useRef(companions)
  useEffect(() => { companionsRef.current = companions }, [companions])
  const isOptionNoBudget = useCallback(
    (option: SearchOption) => isDeptNoBudget((option as DepartmentDTO)?.number),
    [isDeptNoBudget]
  )

  // 移动端 bottom popup 状态
  const [mobilePopupVisible, setMobilePopupVisible] = useState(false)
  const [mobileEditItem, setMobileEditItem] = useState<{
    id: string | number | null  // null = 新增
    nameId: string
    name: string
    displayNumber: string
    departmentId: string
    department: string
    estimatedAmount: number | undefined
  }>({ id: null, nameId: '', name: '', displayNumber: '', departmentId: '', department: '', estimatedAmount: undefined })

  /** 格式化员工展示文本：姓名-工号-域账号，缺失字段自动跳过 */
  const formatEmployeeDisplay = (name: string, displayNumber: string, domain: string): string =>
    [name, displayNumber, domain].filter(Boolean).join('-')

  // 编辑中的行（临时数据，包括已保存和新增的空行）
  const [editingRows, setEditingRows] = useState<Array<{
    id: string | number
    nameId: string
    name: string
    displayNumber: string
    departmentId: string
    department: string
    estimatedAmount: number | undefined
    isNew: boolean
  }>>([])

  /**
   * 验证同行人数据
   * 检查是否有部分填写的行（只填了姓名或只填了部门）
   * 不检查完全空的行
   */
  const validateCompanions = useCallback(() => {
    // 检查是否有部分填写的行（排除完全空的行）
    const partiallyFilledRow = editingRows.find(row => {
      const hasName = !!row.nameId
      const hasDepartment = !!row.departmentId
      const isEmpty = !row.nameId && !row.departmentId

      // 如果完全为空，不认为是错误（允许空行存在）
      // 如果一个填了一个没填，就是部分填写 - 这是错误
      return !isEmpty && ((hasName && !hasDepartment) || (!hasName && hasDepartment))
    })

    if (partiallyFilledRow) {
      const hasName = !!partiallyFilledRow.nameId
      const message = hasName
        ? '请为同行人选择费用承担部门'
        : '请为费用承担部门选择同行人姓名'
      return { isValid: false, message }
    }

    // 检查已填写姓名和部门的行是否填写了预估费用
    const missingAmount = editingRows.find(row =>
      row.nameId && row.departmentId && (row.estimatedAmount == null || row.estimatedAmount === undefined)
    )
    if (missingAmount) {
      return { isValid: false, message: '请填写同行人预估费用' }
    }

    return { isValid: true }
  }, [editingRows])

  // 当编辑行变化时，触发验证
  React.useEffect(() => {
    if (onValidationChange) {
      const { isValid, message } = validateCompanions()
      onValidationChange(isValid, message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRows])

  // 初始化编辑行：包括已有数据 + 一个空行（仅在组件挂载时执行一次）
  React.useEffect(() => {
    const existingRows = companions.map(c => ({
      id: c.id!,
      nameId: c.nameId || '',
      name: c.name,
      displayNumber: '',  // 编辑模式加载时后端不返回工号，降级展示
      departmentId: c.department,
      department: c.departmentName || '',
      estimatedAmount: c.estimatedAmount != null ? Number(c.estimatedAmount) : undefined,
      isNew: false,
    }))

    // 如果没有已有数据，添加一个空行供用户输入
    if (existingRows.length === 0) {
      existingRows.push({
        id: `new_${Date.now()}`,
        nameId: '',
        name: '',
        displayNumber: '',
        departmentId: '',
        department: '',
        estimatedAmount: undefined,
        isNew: true,
      })
    }

    setEditingRows(existingRows)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 空依赖数组，仅在组件挂载时执行一次

  /**
   * 搜索员工 - 直接复用 API 服务
   */
  const handleSearchEmployees = useCallback(
    async (keyword: string) => {
      try {
        const results = await apiClient.searchEmployees(keyword)
        return results
      } catch (error) {
        console.error('搜索员工失败:', error)
        return []
      }
    },
    []
  )

  /**
   * 搜索部门 - 直接复用 API 服务
   */
  const handleSearchDepartments = useCallback(
    async (keyword: string) => {
      try {
        // 优先使用员工的 collection 字段（如 "GSX01"），fallback 到从部门路径推导
        const collection = applicantCollection || getCollectionFromDeptId(applicantDeptId)
        const results = await apiClient.searchDepartments(keyword, collection)
        return results
      } catch (error) {
        console.error('搜索部门失败:', error)
        return []
      }
    },
    [applicantCollection, applicantDeptId]
  )

  /**
   * 处理预估费用输入
   */
  const handleEstimatedAmountChange = (rowId: string | number, value: string) => {
    const numValue = value === '' ? undefined : Math.min(9999999999, Math.max(0, parseFloat(value) || 0))
    // functional update 避免覆盖并发 async 操作（如员工选择）对 editingRows 的变更
    setEditingRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, estimatedAmount: numValue } : row
    ))
    // companions 用 ref 获取最新值，防止快速输入时覆盖并发变更
    const updatedCompanions = companionsRef.current.map(c =>
      c.id === rowId ? { ...c, estimatedAmount: numValue } : c
    )
    onChange(updatedCompanions)
  }

  /**
   * 处理员工选择
   * 自动填充该员工所在的部门到"费用承担部门"字段
   * 如果员工数据中没有部门名称，则根据部门编号查询部门信息
   */
  const handleEmployeeSelect = async (
    rowId: string | number,
    value: string,
    label: string,
    option?: SearchOption
  ) => {
    // 处理清空情况（value 为空字符串或 undefined）
    if (!value || value.trim() === '') {
      const updatedRows = editingRows.map(row => {
        if (row.id === rowId) {
          return {
            ...row,
            nameId: '',
            name: '',
            displayNumber: '',
            departmentId: '',
            department: '',
            isNew: true,
          }
        }
        return row
      })
      setEditingRows(updatedRows)

      // 从 companions 中删除这一行（仅当该行之前已保存到 companions 中）
      const updatedCompanions = companions.filter(c => c.id !== rowId)
      if (updatedCompanions.length !== companions.length) {
        onChange(updatedCompanions)
      }
      
      // 强制刷新表单
      setFormKey(prev => prev + 1)
      return
    }

    const employee = option as EmployeeDTO | undefined
    const empName = employee?.commonName || label
    const empId = employee?.domain || value
    const empDisplayNumber = employee?.displayNumber || ''
    const empDeptId = employee?.department || ''
    let empDeptName = employee?.departmentName || ''

    // 员工默认部门命中无差旅预算黑名单 → 不自动填部门，提示用户手动选择
    const skipDeptAutofill = isDeptNoBudget(empDeptId)
    if (skipDeptAutofill) {
      message.info(`同行人「${empName}」默认部门无差旅预算，请手动选择费用承担部门`)
    }

    // 捕获 await 前的行快照，用于后续 companion 逻辑（避免 await 期间 editingRows 被其他行更新导致的 stale closure）
    const rowSnapshot = editingRows.find(r => r.id === rowId)

    // PC 端：员工无 department 字段（empDeptId=''）时，isDeptNoBudget('')=false，
    // 若行已持有一个黑名单部门则需主动清空并提示——与移动端 shouldClearPrevBlacklisted 逻辑对齐
    const prevDeptBlacklisted = !!rowSnapshot?.departmentId && isDeptNoBudget(rowSnapshot.departmentId)
    const shouldClearPrevBlacklisted = prevDeptBlacklisted && !empDeptId
    if (shouldClearPrevBlacklisted) {
      message.warning('该员工暂无默认部门，已清空原有无差旅预算部门，请重新选择')
    }

    // 如果有部门ID但没有部门名称，则查询部门信息（命中黑名单时跳过查询）
    if (!shouldClearPrevBlacklisted && !skipDeptAutofill && empDeptId && !empDeptName) {
      try {
        const departments = await handleSearchDepartments(empDeptId)
        if (departments && departments.length > 0) {
          const matched = departments.find((d: DepartmentDTO) => d.number === empDeptId) || departments[0]
          empDeptName = matched.name || matched.shortName || matched.number || ''
        }
      } catch (error) {
        console.error('查询部门信息失败:', error)
      }
    }

    // 统一计算目标部门值（与移动端逻辑对齐）
    // shouldClearPrevBlacklisted=true → 清空；否则基于快照：已有部门则保留，否则按黑名单决定是否自动填充
    const resolvedDeptId = shouldClearPrevBlacklisted ? ''
      : rowSnapshot?.departmentId
        ? rowSnapshot.departmentId
        : (skipDeptAutofill ? '' : empDeptId)
    const resolvedDeptName = shouldClearPrevBlacklisted ? ''
      : rowSnapshot?.departmentId
        ? (rowSnapshot.department || '')
        : (skipDeptAutofill ? '' : empDeptName)

    // functional update：避免覆盖 await 期间其他行的并发变更
    // shouldClearPrevBlacklisted=true 时强制清空，忽略 row 当前值
    setEditingRows(prev => prev.map(row => {
      if (row.id !== rowId) return row
      return {
        ...row,
        nameId: empId,
        name: empName,
        displayNumber: empDisplayNumber,
        departmentId: shouldClearPrevBlacklisted ? '' : (row.departmentId || resolvedDeptId),
        department: shouldClearPrevBlacklisted ? '' : (row.departmentId ? row.department : resolvedDeptName),
      }
    }))

    // companion 逻辑使用相同的 resolvedDeptId/resolvedDeptName，保证与 editingRows 一致
    // companions 通过 ref 获取最新值（Fix2：避免 await 后 companions 闭包陈旧）
    const latestCompanions = companionsRef.current
    const currentRow = rowSnapshot
      ? { ...rowSnapshot, nameId: empId, name: empName, displayNumber: empDisplayNumber,
          departmentId: resolvedDeptId, department: resolvedDeptName }
      : null
    if (currentRow && currentRow.nameId && currentRow.departmentId) {
      if (currentRow.isNew) {
        if (latestCompanions.length >= MAX_COMPANIONS) {
          message.warning('同行人数量已超出限制，最多支持添加 40 位同行人')
          return
        }
        const newCompanion = {
          id: `companion_${Date.now()}`,
          nameId: currentRow.nameId,
          name: currentRow.name,
          department: currentRow.departmentId,
          departmentName: currentRow.department,
          estimatedAmount: currentRow.estimatedAmount,
        }
        onChange([...latestCompanions, newCompanion])
        setEditingRows(prev => prev.map(r =>
          r.id === rowId ? { ...r, isNew: false, id: newCompanion.id } : r
        ))
      } else {
        const existsInCompanions = latestCompanions.some(c => c.id === rowId)
        if (existsInCompanions) {
          onChange(latestCompanions.map(c => c.id === rowId
            ? { ...c, nameId: currentRow.nameId, name: currentRow.name,
                department: currentRow.departmentId, departmentName: currentRow.department }
            : c
          ))
        } else {
          if (latestCompanions.length >= MAX_COMPANIONS) {
            message.warning('同行人数量已超出限制，最多支持添加 40 位同行人')
            return
          }
          const newCompanion = {
            id: `companion_${Date.now()}`,
            nameId: currentRow.nameId,
            name: currentRow.name,
            department: currentRow.departmentId,
            departmentName: currentRow.department,
            estimatedAmount: currentRow.estimatedAmount,
          }
          onChange([...latestCompanions, newCompanion])
          setEditingRows(prev => prev.map(r =>
            r.id === rowId ? { ...r, isNew: false, id: newCompanion.id } : r
          ))
        }
      }
    }
  }

  /**
   * 处理部门选择
   */
  const handleDepartmentSelect = (
    rowId: string | number,
    value: string,
    label: string,
    option?: SearchOption
  ) => {
    // 处理清空情况（value 为空字符串或 undefined）
    if (!value || value.trim() === '') {
      const updatedRows = editingRows.map(row => {
        if (row.id === rowId) {
          return { ...row, departmentId: '', department: '' }
        }
        return row
      })
      setEditingRows(updatedRows)

      // 如果员工也被清空了，从 companions 中删除这一行
      const currentRow = updatedRows.find(r => r.id === rowId)
      if (currentRow && !currentRow.nameId) {
        const updatedCompanions = companions.filter(c => c.id !== rowId)
        if (updatedCompanions.length !== companions.length) {
          onChange(updatedCompanions)
        }
      }
      
      // 强制刷新表单
      setFormKey(prev => prev + 1)
      return
    }

    const department = option as DepartmentDTO | undefined
    const deptId = department?.number || value

    // 命中黑名单 → 弹提示拦截，不更新状态（与下拉列表点击拦截双保险）
    if (isDeptNoBudget(deptId)) {
      message.warning(NO_BUDGET_HINT)
      return
    }

    // 更新该行的 departmentId 和 department
    const updatedRows = editingRows.map(row => {
      if (row.id === rowId) {
        return { ...row, departmentId: deptId, department: label }
      }
      return row
    })
    setEditingRows(updatedRows)

    // 如果该行的员工和部门都已填写，保存到 companions
    const currentRow = updatedRows.find(r => r.id === rowId)
    if (currentRow && currentRow.nameId && currentRow.departmentId) {
      // 如果是新行，添加到 companions
      if (currentRow.isNew) {
        if (companions.length >= MAX_COMPANIONS) {
          message.warning('同行人数量已超出限制，最多支持添加 40 位同行人')
          return
        }
        const newCompanion = {
          id: `companion_${Date.now()}`,
          nameId: currentRow.nameId,
          name: currentRow.name,
          department: currentRow.departmentId,
          departmentName: currentRow.department,
          estimatedAmount: currentRow.estimatedAmount,
        }
        const newCompanions = [...companions, newCompanion]
        onChange(newCompanions)

        // 将该行标记为已保存
        const finalRows = updatedRows.map(r => {
          if (r.id === rowId) {
            return { ...r, isNew: false, id: newCompanion.id }
          }
          return r
        })
        setEditingRows(finalRows)
      } else {
        // 如果是已保存的行，需要检查该行是否还存在于 companions 中
        const existsInCompanions = companions.some(c => c.id === rowId)

        if (existsInCompanions) {
          // 存在：更新 companions
          const updatedCompanions = companions.map(c => {
            if (c.id === rowId) {
              return {
                ...c,
                department: currentRow.departmentId,
                departmentName: currentRow.department,
              }
            }
            return c
          })
          onChange(updatedCompanions)
        } else {
          // 不存在：作为新记录添加（这种情况发生在清空后重新填写）
          if (companions.length >= MAX_COMPANIONS) {
            message.warning('同行人数量已超出限制，最多支持添加 40 位同行人')
            return
          }
          const newCompanion = {
            id: `companion_${Date.now()}`,
            nameId: currentRow.nameId,
            name: currentRow.name,
            department: currentRow.departmentId,
            departmentName: currentRow.department,
            estimatedAmount: currentRow.estimatedAmount,
          }
          const newCompanions = [...companions, newCompanion]
          onChange(newCompanions)

          // 更新该行的 ID 和状态
          const finalRows = updatedRows.map(r => {
            if (r.id === rowId) {
              return { ...r, isNew: false, id: newCompanion.id }
            }
            return r
          })
          setEditingRows(finalRows)
        }
      }
    }
  }

  /**
   * 点击"+ 添加"按钮 - 添加新的空行
   */
  const handleAddNewRow = () => {
    if (disabled) return
    if (companions.length >= MAX_COMPANIONS) {
      message.warning('同行人数量已超出限制，最多支持添加 40 位同行人')
      return
    }
    const newRow = {
      id: `new_${Date.now()}`,
      nameId: '',
      name: '',
      displayNumber: '',
      departmentId: '',
      department: '',
      estimatedAmount: undefined,
      isNew: true,
    }
    // functional update 避免覆盖并发 async handleEmployeeSelect 的变更
    setEditingRows(prev => [...prev, newRow])
  }

  /**
   * 复制某一行
   */
  const handleCopyRow = (rowId: string | number) => {
    if (disabled) return
    try {
      // 用 ref 获取最新 companions，与 handleEmployeeSelect 保持一致
      const latestCompanions = companionsRef.current
      if (latestCompanions.length >= MAX_COMPANIONS) {
        message.warning('同行人数量已超出限制，最多支持添加 40 位同行人')
        return
      }
      const sourceRow = editingRows.find(r => r.id === rowId)
      if (!sourceRow) {
        message.error('复制失败，请重试')
        return
      }

      if (sourceRow.nameId && sourceRow.departmentId) {
        const newCompanion = {
          id: `companion_${Date.now()}`,
          nameId: sourceRow.nameId,
          name: sourceRow.name,
          department: sourceRow.departmentId,
          departmentName: sourceRow.department,
          estimatedAmount: sourceRow.estimatedAmount,
        }
        onChange([...latestCompanions, newCompanion])

        const newRow = {
          id: newCompanion.id,
          nameId: sourceRow.nameId,
          name: sourceRow.name,
          displayNumber: sourceRow.displayNumber,
          departmentId: sourceRow.departmentId,
          department: sourceRow.department,
          estimatedAmount: sourceRow.estimatedAmount,
          isNew: false,
        }
        // functional update 避免覆盖并发 async handleEmployeeSelect 的变更
        setEditingRows(prev => [...prev, newRow])
      } else {
        const newRow = {
          id: `new_${Date.now()}`,
          nameId: sourceRow.nameId,
          name: sourceRow.name,
          displayNumber: sourceRow.displayNumber,
          departmentId: sourceRow.departmentId,
          department: sourceRow.department,
          estimatedAmount: sourceRow.estimatedAmount,
          isNew: true,
        }
        setEditingRows(prev => [...prev, newRow])
      }

      setFormKey(prev => prev + 1)
      message.success('复制成功')
    } catch {
      message.error('复制失败，请重试')
    }
  }

  /**
   * 删除某一行
   */
  const handleDeleteRow = (rowId: string | number) => {
    if (disabled) return
    try {
      const updatedEditingRows = editingRows.filter(r => r.id !== rowId)
      const updatedCompanions = companions.filter(c => c.id !== rowId)
      onChange(updatedCompanions)
      setEditingRows(updatedEditingRows)
      message.success('删除成功')
    } catch {
      message.error('删除失败，请重试')
    }
  }

  /**
   * 移动端：打开新增弹窗
   */
  const handleMobileAdd = () => {
    if (disabled) return
    if (companions.length >= MAX_COMPANIONS) {
      message.warning('同行人数量已超出限制，最多支持添加 40 位同行人')
      return
    }
    setMobileEditItem({ id: null, nameId: '', name: '', displayNumber: '', departmentId: '', department: '', estimatedAmount: undefined })
    setMobilePopupVisible(true)
  }

  /**
   * 移动端：打开编辑弹窗
   */
  const handleMobileEdit = (companion: TravelCompanion) => {
    if (disabled) return
    setMobileEditItem({
      id: companion.id!,
      nameId: companion.nameId || '',
      name: companion.name,
      displayNumber: '',
      departmentId: companion.department,
      department: companion.departmentName || '',
      estimatedAmount: companion.estimatedAmount != null ? Number(companion.estimatedAmount) : undefined,
    })
    setMobilePopupVisible(true)
  }

  /**
   * 移动端：确认弹窗，写入 companions 并同步 editingRows
   */
  const handleMobileConfirm = () => {
    const { id, nameId, name, displayNumber, departmentId, department, estimatedAmount } = mobileEditItem
    if (!nameId || !departmentId) {
      message.warning('请完整填写同行人和费用承担部门')
      return
    }

    if (id === null) {
      // 新增：用 ref 获取最新 companions，与 handleEmployeeSelect 保持一致
      const newId = `companion_${Date.now()}`
      const newCompanion: TravelCompanion = { id: newId, nameId, name, department: departmentId, departmentName: department, estimatedAmount }
      onChange([...companionsRef.current, newCompanion])
      // 同步 editingRows，保持 PC 端一致
      setEditingRows(prev => {
        const withoutBlank = prev.filter(r => !(r.isNew && !r.nameId && !r.departmentId))
        return [...withoutBlank, { id: newId, nameId, name, displayNumber, departmentId, department, estimatedAmount, isNew: false }]
      })
    } else {
      // 编辑：用 ref 获取最新 companions，避免并发更新（如 handleEstimatedAmountChange）被覆盖
      onChange(companionsRef.current.map(c => c.id === id ? { ...c, nameId, name, department: departmentId, departmentName: department, estimatedAmount } : c))
      setEditingRows(prev => prev.map(r => r.id === id ? { ...r, nameId, name, displayNumber, departmentId, department, estimatedAmount } : r))
    }
    setMobilePopupVisible(false)
  }

  /**
   * 格式化员工选项，显示头像和双行布局
   */
  const formatEmployeeOption = (option: SearchOption) => {
    const employee = option as EmployeeDTO
    return (
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          {employee.avatarUrl ? (
            <img
              src={employee.avatarUrl}
              alt={employee.commonName}
              className="w-8 h-8 rounded-full object-cover bg-gray-200"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling?.classList.remove('hidden')
              }}
            />
          ) : null}
          <div
            className={`w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-600 ${
              employee.avatarUrl ? 'hidden' : ''
            }`}
          >
            {employee.commonName?.[0] || '?'}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {employee.commonName}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {employee.displayNumber || ''}
            {employee.displayNumber && employee.domain ? '-' : ''}
            {employee.domain || ''}
          </p>
        </div>
      </div>
    )
  }

  /**
   * 格式化部门选项
   */
  const formatDepartmentOption = (option: SearchOption) => {
    const department = option as DepartmentDTO
    const deptName = department.name || department.shortName || ''
    const deptPath = department.departmentPathName || ''

    return (
      <div className="flex flex-col">
        <p className="text-sm font-medium text-gray-900 truncate">
          {deptName}
        </p>
        {deptPath && (
          <p className="text-xs text-gray-400 truncate">
            {deptPath}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-clay-md">
      <h4 className="heading-2">
        同行人明细
      </h4>

      {/* 桌面端表格 */}
      <div className="hidden md:block w-full overflow-x-auto -mx-1 px-1">
        <table className="w-full text-clay-sm border-collapse" style={{ minWidth: '520px' }}>
          <thead>
            <tr className="border-b border-slate-200 bg-gray-50">
              <th className="text-left py-3 px-4 font-medium text-slate-700 w-12">
                #
              </th>
              <th className="text-left py-3 px-4 font-medium text-slate-700">
                同行人 <span className="text-red-500">*</span>
              </th>
              <th className="text-left py-3 px-4 font-medium text-slate-700">
                费用承担部门 <span className="text-red-500">*</span>
                <Tooltip title="仅支持同主体部门，不允许跨主体搜索。">
                  <span className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs cursor-help">
                    i
                  </span>
                </Tooltip>
              </th>
              <th className="text-left py-3 px-4 font-medium text-slate-700 w-36">
                预估费用（元）<span className="text-red-500">*</span>
              </th>
              <th className="text-left py-3 px-4 font-medium text-slate-700 w-32">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {editingRows.map((row, index) => (
              <tr
                key={row.id}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="py-2 px-4 text-gray-600 align-top pt-4">
                  {index + 1}
                </td>
                <td className="py-2 px-4 align-top">
                  <div>
                    <AsyncSearchSelect
                      key={`employee-${row.id}-${formKey}`}
                      label=""
                      value={row.nameId}
                      displayText={formatEmployeeDisplay(row.name, row.displayNumber, row.nameId)}
                      onSearch={handleSearchEmployees}
                      onChange={(value, label, option) =>
                        handleEmployeeSelect(row.id, value, label, option)
                      }
                      placeholder="请输入"
                      required={false}
                      formatOption={formatEmployeeOption}
                      disabled={disabled}
                    />
                  </div>
                </td>
                <td className="py-2 px-4 align-top">
                  <div>
                    <AsyncSearchSelect
                      key={`department-${row.id}-${formKey}`}
                      label=""
                      value={row.departmentId}
                      displayText={row.department}
                      onSearch={handleSearchDepartments}
                      onChange={(value, label, option) =>
                        handleDepartmentSelect(row.id, value, label, option)
                      }
                      placeholder="请输入"
                      required={false}
                      formatOption={formatDepartmentOption}
                      disabled={disabled}
                      isOptionDisabled={isOptionNoBudget}
                      disabledLabel={NO_BUDGET_TAG}
                      disabledMessage={NO_BUDGET_HINT}
                    />
                  </div>
                </td>
                <td className="py-2 px-4 align-top pt-3">
                  <input
                    type="number"
                    min="0"
                    max="9999999999"
                    placeholder="0"
                    className={`clay-input w-full ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`}
                    value={row.estimatedAmount ?? ''}
                    onChange={(e) => handleEstimatedAmountChange(row.id, e.target.value)}
                    disabled={disabled}
                  />
                </td>
                <td className="py-2 px-4 align-top pt-4">
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleCopyRow(row.id)}
                      disabled={disabled}
                      className={`font-medium transition-colors text-sm ${disabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-500 hover:text-red-700'}`}
                      title="复制此行"
                    >
                      复制
                    </button>
                    <button
                      onClick={() => handleDeleteRow(row.id)}
                      disabled={disabled}
                      className={`font-medium transition-colors text-sm ${disabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-500 hover:text-red-700'}`}
                      title="删除此行"
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 移动端只读卡片列表 */}
      <div className="md:hidden space-y-3">
        {companions.length === 0 && (
          <div className="text-center py-6 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
            暂无同行人，点击下方「添加」
          </div>
        )}
        {companions.map((companion, index) => (
          <div
            key={companion.id}
            className={`clay-input flex items-center justify-between ${disabled ? 'cursor-default' : 'cursor-pointer active:bg-blue-50'}`}
            onClick={disabled ? undefined : () => handleMobileEdit(companion)}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                <span className="text-gray-400 mr-1">#{index + 1}</span>{companion.name}
              </p>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {companion.departmentName || companion.department}
              </p>
              {companion.estimatedAmount != null && (
                <p className="text-xs text-gray-500 mt-0.5">
                  预估费用：{companion.estimatedAmount} 元
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 ml-3 flex-shrink-0">
              {!disabled && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCopyRow(companion.id!) }}
                    className="text-sm text-blue-500 font-medium"
                  >复制</button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteRow(companion.id!) }}
                    className="text-sm text-red-500 font-medium"
                  >删除</button>
                </>
              )}
              {!disabled && (
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 移动端新增/编辑 Popup */}
      <Popup
        visible={mobilePopupVisible}
        onMaskClick={() => setMobilePopupVisible(false)}
        position="bottom"
        bodyStyle={{ borderTopLeftRadius: '16px', borderTopRightRadius: '16px', minHeight: '60vh' }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-gray-900">
              {mobileEditItem.id === null ? '添加同行人' : '编辑同行人'}
            </h3>
            <button onClick={() => setMobilePopupVisible(false)} className="text-gray-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <AsyncSearchSelectResponsive
              label="同行人"
              value={mobileEditItem.nameId}
              displayText={formatEmployeeDisplay(mobileEditItem.name, mobileEditItem.displayNumber, mobileEditItem.nameId)}
              onSearch={handleSearchEmployees}
              onChange={(value, label, option) => {
                const emp = option as EmployeeDTO | undefined
                if (!value) {
                  setMobileEditItem(prev => ({ ...prev, nameId: '', name: '', displayNumber: '' }))
                  return
                }
                const empName = emp?.commonName || label
                const empDept = emp?.department            // 可能为 undefined
                const empDeptName = emp?.departmentName || ''
                const skipDeptAutofill = isDeptNoBudget(empDept)
                if (skipDeptAutofill) {
                  message.info(`同行人「${empName}」默认部门无差旅预算，请手动选择费用承担部门`)
                }

                // Fix6：empDept 为 undefined（员工无部门）时，若旧部门在黑名单则清空并提示，
                // 防止黑名单部门因 isDeptNoBudget(undefined)=false 而静默保留
                const prevDeptBlacklisted = !!mobileEditItem.departmentId && isDeptNoBudget(mobileEditItem.departmentId)
                const shouldClearPrevBlacklisted = prevDeptBlacklisted && !empDept
                if (shouldClearPrevBlacklisted) {
                  message.warning('该员工暂无默认部门，已清空原有无差旅预算部门，请重新选择')
                }

                setMobileEditItem(prev => {
                  if (shouldClearPrevBlacklisted) {
                    return {
                      ...prev,
                      nameId: emp?.domain || value,
                      name: empName,
                      displayNumber: emp?.displayNumber || '',
                      departmentId: '',
                      department: '',
                    }
                  }
                  // Fix5：dept name 为空时（即使 deptId 已有值）也尝试自动补全同一部门的名称
                  const shouldAutofillName = (!prev.departmentId || !prev.department)
                    && !skipDeptAutofill && empDeptName
                    && (!prev.departmentId || empDept === prev.departmentId)
                  return {
                    ...prev,
                    nameId: emp?.domain || value,
                    name: empName,
                    displayNumber: emp?.displayNumber || '',
                    // 仅当部门未填、且员工默认部门不在黑名单时才自动带出
                    departmentId: !prev.departmentId && !skipDeptAutofill && empDept ? empDept : prev.departmentId,
                    department: shouldAutofillName ? empDeptName : prev.department,
                  }
                })

                // 若员工有部门 ID 但无部门名称，异步查询后回填显示名（与 PC 端逻辑对齐）
                if (!skipDeptAutofill && !shouldClearPrevBlacklisted && empDept && !empDeptName) {
                  handleSearchDepartments(empDept).then(departments => {
                    if (departments && departments.length > 0) {
                      const matched = departments.find((d: DepartmentDTO) => d.number === empDept) || departments[0]
                      const resolvedName = matched.name || matched.shortName || matched.number || ''
                      if (resolvedName) {
                        setMobileEditItem(prev => ({
                          ...prev,
                          // 仅在名称仍为空时回填，避免覆盖用户已手动修改的名称
                          department: prev.department || resolvedName,
                        }))
                      }
                    }
                  }).catch(() => { /* 查询失败降级，保留空名称 */ })
                }
              }}
              formatOption={formatEmployeeOption}
              placeholder="输入姓名搜索..."
              required={true}
            />

            <div>
              <AsyncSearchSelectResponsive
                label="费用承担部门"
                value={mobileEditItem.departmentId}
                displayText={mobileEditItem.department}
                onSearch={handleSearchDepartments}
                onChange={(value, label, option) => {
                  const dept = option as DepartmentDTO | undefined
                  const deptId = dept?.number || value
                  if (isDeptNoBudget(deptId)) {
                    message.warning(NO_BUDGET_HINT)
                    return
                  }
                  setMobileEditItem(prev => ({
                    ...prev,
                    departmentId: deptId,
                    department: label,
                  }))
                }}
                formatOption={formatDepartmentOption}
                placeholder="输入部门名称搜索..."
                required={true}
                isOptionDisabled={isOptionNoBudget}
                disabledLabel={NO_BUDGET_TAG}
                disabledMessage={NO_BUDGET_HINT}
              />
              <p className="text-xs text-gray-400 mt-1">仅支持同主体部门</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">预估费用（元）<span className="text-red-500">*</span></label>
              <input
                type="number"
                min="0"
                max="9999999999"
                placeholder="0"
                className="clay-input w-full"
                value={mobileEditItem.estimatedAmount ?? ''}
                onChange={(e) => {
                  const val = e.target.value
                  const numValue = val === '' ? undefined : Math.min(9999999999, Math.max(0, parseFloat(val) || 0))
                  setMobileEditItem(prev => ({ ...prev, estimatedAmount: numValue }))
                }}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              className="flex-1 clay-btn-ghost"
              onClick={() => setMobilePopupVisible(false)}
            >取消</button>
            <button
              className="flex-1 clay-btn-primary"
              onClick={handleMobileConfirm}
            >确认</button>
          </div>
        </div>
      </Popup>

      {/* 添加按钮：PC 端用原有行添加逻辑，移动端打开弹窗 */}
      <button
        onClick={handleAddNewRow}
        disabled={disabled || companions.length >= MAX_COMPANIONS}
        className={`hidden md:flex font-medium transition-colors items-center gap-1 text-sm ${
          (disabled || companions.length >= MAX_COMPANIONS)
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-red-500 hover:text-red-700'
        }`}
      >
        <span className="text-lg">+</span> 添加
      </button>
      <button
        onClick={handleMobileAdd}
        disabled={disabled || companions.length >= MAX_COMPANIONS}
        className={`md:hidden font-medium transition-colors flex items-center gap-1 text-sm ${
          (disabled || companions.length >= MAX_COMPANIONS)
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-red-500 hover:text-red-700'
        }`}
      >
        <span className="text-lg">+</span> 添加
      </button>
    </div>
  )
}

export default CompanionList
